import { useId } from 'react';

import type { RateCardItem } from '@mds/shared/types/rate-card';
import type {
  ResourceBookingRules,
  ResourceDesk,
  ResourceKind,
  ResourceMediaItem,
  ResourceWeekday,
} from '@mds/shared/types/resource';

import GalleryPicker from '@/components/files/gallery-picker';
import ChipPicker from '@/components/ui/chip-picker';
import Chip from '@/components/ui/chip';
import Field, { FieldError, inputCls, selectCls } from '@/components/ui/field';
import Select from '@mds/shared/ui/select';
import { hourLabel } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import type { FieldBinder } from '@/lib/validation';

import {
  AMENITY_POOL,
  BOOKING_RULE_FIELDS,
  type HoursRow,
  type ResourceDraft,
} from './resource-draft';

const DAY_LABELS: Record<ResourceWeekday, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

/** Midnight as a closing time reads as the end of the day, not its start. */
const hourOption = (hour: number) => ({
  value: String(hour),
  label: hour === 24 ? 'Midnight' : hourLabel(hour),
});
const OPEN_OPTIONS = Array.from({ length: 24 }, (_, h) => hourOption(h));
const CLOSE_OPTIONS = Array.from({ length: 24 }, (_, h) => hourOption(h + 1));

/**
 * A room's week, one row a day. Whole hours only — the API stores nothing
 * finer — and the booking board draws its open window straight from this.
 */
function HoursEditor({
  rows,
  onChange,
  field,
}: {
  rows: HoursRow[];
  onChange: (next: HoursRow[]) => void;
  field: FieldBinder;
}) {
  const id = useId();
  const setRow = (weekday: ResourceWeekday, patch: Partial<HoursRow>) =>
    onChange(rows.map((row) => (row.weekday === weekday ? { ...row, ...patch } : row)));

  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
        Opening hours
      </legend>
      <p className="m-0 mt-2 mb-3 text-base text-ink-muted">
        Bookings can only be made inside these hours. A change that would leave a confirmed booking
        outside them is refused.
      </p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {rows.map((row, i) => {
          // A day is one grid row rather than a Field, so it wires its own
          // error: leaving anything in the row reveals it, on the closing time.
          const { error, onBlur } = field(`hours.${i}.closeHour`);
          const errorId = `${id}-${row.weekday}-error`;
          return (
            <li
              key={row.weekday}
              onBlur={onBlur}
              className="grid grid-cols-[7rem_auto] items-center gap-x-4 gap-y-2 sm:grid-cols-[7rem_auto_1fr_1fr]"
            >
              <span className="text-base font-semibold">{DAY_LABELS[row.weekday]}</span>
              <label className="flex items-center gap-2 text-base">
                <input
                  type="checkbox"
                  className="h-5 w-5 accent-brand-edge"
                  checked={!row.isClosed}
                  onChange={(e) => setRow(row.weekday, { isClosed: !e.target.checked })}
                />
                Open
              </label>
              {row.isClosed ? (
                <span className="text-base text-ink-muted sm:col-span-2">Closed all day</span>
              ) : (
                <>
                  <Select
                    variant="field"
                    className={selectCls}
                    value={String(row.openHour)}
                    onChange={(v) => setRow(row.weekday, { openHour: Number(v) })}
                    options={OPEN_OPTIONS}
                    aria-label={`${DAY_LABELS[row.weekday]} opens`}
                  />
                  <Select
                    variant="field"
                    className={selectCls}
                    value={String(row.closeHour)}
                    onChange={(v) => setRow(row.weekday, { closeHour: Number(v) })}
                    options={CLOSE_OPTIONS}
                    aria-label={`${DAY_LABELS[row.weekday]} closes`}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                  />
                </>
              )}
              {error && (
                <span className="sm:col-span-4">
                  <FieldError id={errorId}>{error}</FieldError>
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}

/**
 * The four lines under "Booking rules" on the space's page. Each is optional:
 * an empty one keeps the standard wording for this kind of space, which the
 * placeholder shows, so clearing a line is how to go back to it.
 */
function BookingRulesEditor({
  value,
  defaults,
  supported,
  onChange,
  field,
}: {
  value: ResourceBookingRules;
  /** The standard wording; absent for a space that does not exist yet. */
  defaults?: ResourceBookingRules;
  /** False against an API without the field: shown, but locked and explained. */
  supported: boolean;
  onChange: (next: ResourceBookingRules) => void;
  field: FieldBinder;
}) {
  return (
    <fieldset className="m-0 flex flex-col gap-4 border-0 p-0">
      <legend className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
        Booking rules
      </legend>
      <p className="m-0 -mt-2 text-base text-ink-muted">
        Shown on the space&apos;s page on the website. Leave a line empty to keep the standard
        wording{defaults ? ', shown greyed out' : ''}.
      </p>
      {!supported && (
        <p className="m-0 rounded-xl border border-warn-edge bg-warn-wash px-4 py-3 text-base text-warn-text">
          The server this admin is connected to does not store booking rules yet. They can be edited
          here once the backend update is deployed and its migration has run.
        </p>
      )}
      {BOOKING_RULE_FIELDS.map(({ key, label }) => (
        <Field key={key} label={label} {...field(`bookingRules.${key}`)}>
          <textarea
            rows={2}
            maxLength={1000}
            className={inputCls}
            value={value[key]}
            placeholder={defaults?.[key]}
            disabled={!supported}
            onChange={(e) => onChange({ ...value, [key]: e.target.value })}
          />
        </Field>
      ))}
    </fieldset>
  );
}

/**
 * Everything the API will let an administrator change about one space.
 *
 * Price is not among it: a resource points at a rate-card item by its stable
 * id, and the amount is resolved from the active rate-card version at read
 * time. Changing what something costs is a rate-card publish, not an edit here.
 * Keys, kinds, facility links and seat maps are immutable by the same logic —
 * bookings and the seat-map artwork reference them.
 */
export default function ResourceEditor({
  draft,
  onChange,
  creating,
  spaceRates,
  facilityName,
  desks,
  deskBusyId,
  onToggleDesk,
  knownMedia,
  bookingRules,
  field,
}: {
  draft: ResourceDraft;
  onChange: (next: ResourceDraft) => void;
  creating: boolean;
  spaceRates: RateCardItem[];
  facilityName: string;
  desks: ResourceDesk[];
  deskBusyId: string | null;
  onToggleDesk: (desk: ResourceDesk) => void;
  /** The space's current pictures, which the library page may not include. */
  knownMedia?: readonly ResourceMediaItem[];
  /**
   * Whether the API takes booking rules, and its standard wording for this
   * space. An API that predates the field gets the section locked, not hidden.
   */
  bookingRules: { supported: boolean; defaults?: ResourceBookingRules };
  /** The page's validation, checked against `resourceSchema`. */
  field: FieldBinder;
}) {
  const set = (patch: Partial<ResourceDraft>) => onChange({ ...draft, ...patch });
  const isFloor = draft.kind === 'floor';

  // A floor is priced by the day and a room by the hour; the API rejects
  // anything else, so the wrong ones are never offered.
  // Rooms go by the hour or by the block (the café hall's evening), floors by the day.
  const rates = spaceRates.filter((r) =>
    isFloor ? r.unit === 'day' : r.unit === 'hour' || r.unit === 'block',
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap gap-2">
        <Chip>{facilityName}</Chip>
        <Chip>{isFloor ? 'Coworking floor' : 'Room'}</Chip>
      </div>

      {creating && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kind" hint="Fixed once created." required {...field('kind')}>
            <Select
              variant="field"
              className={selectCls}
              value={draft.kind}
              onChange={(kind) =>
                set({
                  kind: kind as ResourceKind,
                  bookingMode: 'direct_request',
                  // The two kinds price off different units, so a rate picked
                  // for one is never valid for the other.
                  rateItemId: '',
                })
              }
              options={[
                { value: 'room', label: 'Room' },
                { value: 'floor', label: 'Coworking floor' },
              ]}
              aria-label="Kind"
            />
          </Field>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" required {...field('name')}>
          <input
            className={inputCls}
            value={draft.name}
            placeholder="Focus Room"
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field
          label="Rate"
          hint={`Priced per ${isFloor ? 'day' : 'hour or block'}. Amounts are set on Packages & pricing.`}
          required
          {...field('rateItemId')}
        >
          <Select
            variant="field"
            className={selectCls}
            value={draft.rateItemId}
            onChange={(rateItemId) => set({ rateItemId })}
            options={rates.map((r) => ({
              value: r.id,
              label: `${r.name} · ${formatCents(r.amountMinor)}/${r.unit}`,
            }))}
            placeholder="Pick a space rate…"
            aria-label="Rate"
          />
        </Field>
      </div>

      <Field
        label="Public request flow"
        hint={
          isFloor
            ? 'Coworking floors always create booking requests.'
            : 'Booking requests go straight to Booking requests. Proposals are reviewed and quoted first.'
        }
        required
        {...field('bookingMode')}
      >
        <Select
          variant="field"
          className={selectCls}
          value={draft.bookingMode}
          disabled={isFloor}
          onChange={(bookingMode) =>
            set({ bookingMode: bookingMode as ResourceDraft['bookingMode'] })
          }
          options={[
            { value: 'direct_request', label: 'Booking request' },
            { value: 'proposal', label: 'Proposal' },
          ]}
          aria-label="Public request flow"
        />
      </Field>

      {isFloor ? (
        creating && (
          <Field
            label="Seat map"
            hint="The artwork this floor renders. Fixed once created."
            required
            {...field('seatMapKey')}
          >
            <Select
              variant="field"
              className={selectCls}
              value={draft.seatMapKey}
              onChange={(seatMapKey) => set({ seatMapKey })}
              options={[
                { value: 'ground', label: 'Ground floor' },
                { value: 'upper', label: 'Upper floor' },
              ]}
              aria-label="Seat map"
            />
          </Field>
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Capacity"
            hint="How many people the room seats."
            required={creating}
            {...field('capacity')}
          >
            <input
              type="number"
              min={1}
              className={inputCls}
              value={draft.capacity}
              onChange={(e) => set({ capacity: e.target.value })}
            />
          </Field>
          <Field
            label="Shortest booking"
            hint="In minutes. 60 unless the room asks for more."
            required={creating}
            {...field('minimumBookingMinutes')}
          >
            <input
              type="number"
              min={15}
              step={15}
              className={inputCls}
              value={draft.minimumBookingMinutes}
              onChange={(e) => set({ minimumBookingMinutes: e.target.value })}
            />
          </Field>
        </div>
      )}

      <Field label="Note" hint="One line, shown under the picker on this page." {...field('note')}>
        <input
          className={inputCls}
          value={draft.note}
          placeholder="Seats 6 · booked by the hour"
          onChange={(e) => set({ note: e.target.value })}
        />
      </Field>

      <Field
        label="Short description"
        hint="A line or two, for the space cards on the website."
        {...field('shortDescription')}
      >
        <textarea
          rows={2}
          maxLength={500}
          className={inputCls}
          value={draft.shortDescription}
          onChange={(e) => set({ shortDescription: e.target.value })}
        />
      </Field>

      <Field
        label="Long description"
        hint="The full write-up on the space's page on the website."
        {...field('longDescription')}
      >
        <textarea
          rows={5}
          className={inputCls}
          value={draft.longDescription}
          onChange={(e) => set({ longDescription: e.target.value })}
        />
      </Field>

      {!isFloor && (
        <HoursEditor rows={draft.hours} onChange={(hours) => set({ hours })} field={field} />
      )}

      <BookingRulesEditor
        value={draft.bookingRules}
        defaults={bookingRules.defaults}
        supported={bookingRules.supported}
        onChange={(rules) => set({ bookingRules: rules })}
        field={field}
      />

      <ChipPicker
        label="Amenities"
        pool={AMENITY_POOL}
        value={draft.amenities}
        onChange={(amenities) => set({ amenities })}
        placeholder="Add an amenity…"
        {...field('amenities', { within: true })}
      />

      <GalleryPicker
        value={draft.mediaFileIds}
        onChange={(mediaFileIds) => set({ mediaFileIds })}
        known={knownMedia}
        optional
        max={20}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Display order"
          hint="Lower sorts first in the picker."
          required
          {...field('displayOrder')}
        >
          <input
            type="number"
            className={inputCls}
            value={draft.displayOrder}
            onChange={(e) => set({ displayOrder: e.target.value })}
          />
        </Field>
        <label className="flex items-center gap-2.5 self-end pb-3.5 text-base">
          <input
            type="checkbox"
            className="h-6 w-6 accent-brand-edge"
            checked={draft.isActive}
            onChange={(e) => set({ isActive: e.target.checked })}
          />
          Bookable
        </label>
      </div>

      {!creating && desks.length > 0 && (
        <fieldset className="m-0 border-0 p-0">
          <legend className="font-mono text-sm font-semibold tracking-wider text-ink-secondary uppercase">
            Desks ({desks.filter((d) => d.isActive).length} of {desks.length} in service)
          </legend>
          <p className="m-0 mt-2 mb-3 text-base text-ink-muted">
            Taking a desk out of service hides it from the floor plan. Desks come from the seat-map
            artwork, so they cannot be added, renamed or reordered here.
          </p>
          <div className="flex flex-wrap gap-2">
            {desks.map((desk) => (
              <button
                key={desk.id}
                type="button"
                disabled={deskBusyId !== null}
                onClick={() => onToggleDesk(desk)}
                aria-pressed={desk.isActive}
                title={desk.isActive ? 'In service (click to withdraw)' : 'Out of service'}
                className={
                  'min-h-(--target-min) rounded-xl border px-3 font-mono text-md transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color) disabled:opacity-60 ' +
                  (desk.isActive
                    ? 'border-brand-edge/40 bg-brand-wash text-brand-strong'
                    : 'border-hairline bg-raised text-ink-muted line-through')
                }
              >
                {desk.deskId}
              </button>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}
